import Trajectory from './Trajectory'
import { ScaleLinear, scaleLinear } from 'd3-scale'
import { ZoomTransform } from 'd3'
import { DashTheme, PointType, TrajectoryPoint, Vector } from '../types'
import { line, curveCatmullRom } from 'd3-shape'
import i18n from 'live/services/i18n'
import theme from 'live/services/theme'
import paper from 'paper'

export default class WBSRender {
    private el: HTMLCanvasElement
    private trajectory: Trajectory
    public bitCorrectionX: number
    public bitCorrectionY: number
    public ctx: CanvasRenderingContext2D
    public transform: ZoomTransform
    public bitDepth: number
    public width: number
    public height: number
    public axisMargin: number
    private dashTheme: DashTheme

    constructor(
        el: HTMLCanvasElement,
        trajectory: Trajectory,
        width: number,
        height: number,
        transform: ZoomTransform,
        axisMargin: number,
        theme: DashTheme
    ) {
        this.el = el
        this.trajectory = trajectory
        this.width = width
        this.height = height
        this.transform = transform
        this.ctx = el.getContext('2d') as CanvasRenderingContext2D
        this.axisMargin = axisMargin
        this.dashTheme = theme

        const deepestYPoint = this.trajectory.getLimits()[1]

        this.ctx.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0)
        // blue bg
        this.ctx.fillStyle = '#c8e9fa'
        this.ctx.fillRect(0, 0, el.clientWidth, el.clientHeight)

        // ground bg
        this.ctx.fillStyle = '#e6cfa1'
        this.ctx.fillRect(
            0,
            axisMargin + this.trajectory.getPoint(0, 0, 'md').y + transform.y,
            el.clientWidth,
            deepestYPoint * 4
        )

        // this save is necessary so the transforms in the context don't accumulate
        this.ctx.save()
        this.ctx.translate(transform.x, axisMargin + transform.y)
        this.ctx.scale(transform.k, transform.k)
    }

    renderMouseOverMarker(md: number, maxArea: number, canvas: HTMLCanvasElement): void {
        if (!canvas) return

        const ctx2 = canvas.getContext('2d')
        if (!ctx2) return
        ctx2.clearRect(0, 0, canvas.width, canvas.height)
        ctx2.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0)

        ctx2.save()
        ctx2.translate(this.transform.x, this.transform.y + this.axisMargin)
        ctx2.scale(this.transform.k, this.transform.k)

        if (md <= (this.trajectory?.maxMD || 0) && maxArea) {
            const p1 = this.trajectory.getPoint(md, -maxArea, 'md')
            const p2 = this.trajectory.getPoint(md, maxArea, 'md')

            ctx2.lineWidth = 1 / this.transform.k
            ctx2.strokeStyle = '#666'
            ctx2.beginPath()
            ctx2.moveTo(p1.x, p1.y)
            ctx2.lineTo(p2.x, p2.y)
            ctx2.stroke()
        }
        ctx2.restore()

        // these 3 clearRects make sure the md indicator doesn't render over the axis
        ctx2.clearRect(0, 0, this.axisMargin, canvas.height)
        ctx2.clearRect(0, 0, canvas.width, this.axisMargin)
        ctx2.clearRect(this.el.clientWidth, 0, -this.axisMargin, canvas.height)
    }

    renderTVDAxis(context: CanvasRenderingContext2D): void {
        // reset position to make the axis start at the default origin point
        context.save()
        this.ctx.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0)

        // axis border
        context.strokeStyle = this.dashTheme === 'light' ? '#000' : '#fff'
        context.lineWidth = 1.5
        context.strokeRect(
            this.axisMargin,
            this.axisMargin,
            this.el.clientWidth - this.axisMargin * 2,
            this.el.clientHeight
        )

        // axis bg
        context.clearRect(0, 0, this.axisMargin, this.el.clientHeight)
        context.clearRect(this.el.clientWidth - this.axisMargin, 0, this.axisMargin, this.el.clientHeight)

        // multiply so the scale goes on a little longer than the well
        const tvdMaxDepth = this.trajectory.maxDepth('tvd') * 20

        // this scale is just used to generate the ticks
        const yAxisTickScale = scaleLinear().domain([-tvdMaxDepth, tvdMaxDepth]).range([0, this.height])

        // the ticks are relative to the limits but also to the transform.
        // this way the closer the zoom is more ticks appear
        const arrayOfTicksInYAxis = yAxisTickScale.ticks(Math.max(tvdMaxDepth * 0.02 * this.transform.k, 5))

        const TICK_MARK_WIDTH = 4
        const TICK_MARK_HEIGHT = 2

        // text styles
        context.fillStyle = this.dashTheme === 'light' ? theme['gray-dark'] : '#fff'
        context.font = '11px arial'
        context.textBaseline = 'middle'

        context.save()
        for (const tvdDepth of arrayOfTicksInYAxis) {
            const originalYPosition = this.trajectory.getPoint(tvdDepth, 0, 'tvd')
            const tickYPosition = originalYPosition.y * this.transform.k + this.transform.y + this.axisMargin

            // left ticks
            context.textAlign = 'right'
            context.fillText(tvdDepth.toString(), this.axisMargin - TICK_MARK_WIDTH * 2, tickYPosition)

            // right ticks
            context.textAlign = 'left'
            context.fillText(
                tvdDepth.toString(),
                this.el?.clientWidth - this.axisMargin + TICK_MARK_WIDTH * 2,
                tickYPosition
            )

            // tick marks
            context.lineWidth = 1
            context.fillRect(this.axisMargin, tickYPosition - TICK_MARK_HEIGHT, -TICK_MARK_WIDTH, TICK_MARK_HEIGHT / 2)
            context.fillRect(
                this.el?.clientWidth - this.axisMargin,
                tickYPosition - TICK_MARK_HEIGHT,
                TICK_MARK_WIDTH,
                TICK_MARK_HEIGHT / 2
            )
        }
        context.restore()

        // this clearRect makes sure the y axis dont overlap with the x axis
        context.clearRect(0, 0, this.el.clientWidth, this.axisMargin)

        // this variable controls the max depth with ticks in the chart
        const xAxisMaxValue = this.trajectory.maxDepth('md') * 20

        const xAxisTickScale = scaleLinear().domain([-xAxisMaxValue, xAxisMaxValue]).range([0, this.width])
        const arrayOfTicksInXAxis = xAxisTickScale.ticks(Math.max(xAxisMaxValue * 0.01 * this.transform.k, 5))

        context.lineWidth = 1
        context.save()
        for (const tick of arrayOfTicksInXAxis) {
            const originalXPosition = this.trajectory.getPoint(0, tick, 'md')
            const tickXPosition = originalXPosition.x * this.transform.k + this.transform.x

            context.textAlign = 'center'
            context.textBaseline = 'bottom'

            // in the x axis the w and h are inverted on purpose
            context.fillText(tick.toString(), tickXPosition, this.axisMargin - TICK_MARK_WIDTH * 2)
            context.fillRect(tickXPosition, this.axisMargin, TICK_MARK_HEIGHT / 2, -TICK_MARK_WIDTH)
        }
        context.restore()

        // these clearRects make sure the x axis dont overlap with the y axis
        context.clearRect(0, 0, this.axisMargin, this.axisMargin)
        context.clearRect(this.el.clientWidth, 0, -this.axisMargin, this.axisMargin)

        // axis labels
        context.save()
        context.textAlign = 'center'
        context.fillStyle = this.dashTheme === 'light' ? theme['gray-dark'] : '#fff'
        const TEXT_MARGIN = 10

        context.textBaseline = 'hanging'
        context.fillText(`${i18n('Equivalent Departure')} (ED)`, this.el.clientWidth / 2, TEXT_MARGIN)

        const axisName = `${i18n('True Vertical Depth')} (TVD)`
        context.translate(0, this.el.clientHeight / 2)
        context.rotate((-90 * Math.PI) / 180)
        context.fillText(axisName, 0, TEXT_MARGIN)

        context.rotate((180 * Math.PI) / 180)

        context.fillText(axisName, 0, -this.el.clientWidth + TEXT_MARGIN)
        context.restore()

        // this is not a duplicate, it is important to restore the save from the beginning of the func
        context.restore()
    }

    renderHorizontalLine(md: number, offsetStart: number, offsetEnd: number): void {
        const p1 = this.trajectory.getPoint(md, offsetStart, 'md')
        const p2 = this.trajectory.getPoint(md, offsetEnd, 'md')
        this.ctx.beginPath()
        this.ctx.moveTo(p1.x, p1.y)
        this.ctx.lineTo(p2.x, p2.y)
        this.ctx.stroke()
    }

    renderArea(mdStart: number, mdEnd: number, offsetStart: number, offsetEnd: number, fillStyle: string): void {
        this.ctx.lineWidth = 1
        const v: Vector[] = this.trajectory.getArea(mdStart, mdEnd, offsetStart, offsetEnd)
        this.ctx.fillStyle = fillStyle
        this.drawArea(v)
    }

    drawArea(vectors: Vector[]): void {
        if (!vectors.length) return

        this.ctx.beginPath()
        for (const p of vectors) {
            if (p === vectors[0]) {
                this.ctx.moveTo(p.x, p.y)
            } else {
                this.ctx.lineTo(p.x, p.y)
            }
        }
        this.ctx.lineTo(vectors[0].x, vectors[0].y)
        this.ctx.fill()
    }

    renderGrid(startOffset: number, endOffset: number, ticks = 6, scale: ScaleLinear<number, number>): void {
        this.ctx.lineWidth = 1
        this.ctx.strokeStyle = '#ccc'
        for (const offset of scale.ticks(ticks)) {
            this.renderLine(this.trajectory.getSegment(0, this.trajectory.maxMD, scale(offset)), '#ccc')
        }

        for (const md of scaleLinear().domain([0, this.trajectory.maxMD]).ticks(10)) {
            this.renderHorizontalLine(md, startOffset, endOffset)
        }

        this.renderLine(this.trajectory.getSegment(0, this.trajectory.maxMD, startOffset), theme['gray-darker'])
        this.ctx.lineWidth = 4
        this.renderLine(this.trajectory.getSegment(0, this.trajectory.maxMD, endOffset), '#b1862f')
        this.ctx.lineWidth = 1
        this.ctx.stroke()
    }

    renderPathLine(vectors: Vector[], strokeStyle = '#000'): void {
        const generator = line<PointType>()
            .x(p => p.x)
            .y(p => p.y)

        this.ctx.strokeStyle = strokeStyle

        generator.context(this.ctx)
        this.ctx.beginPath()
        generator(vectors)
        this.ctx.stroke()
    }

    renderTrajectory(points: TrajectoryPoint[]): void {
        this.renderPathLine(points, '#0a381f')

        let lastY = 0
        for (const p of points) {
            this.ctx.beginPath()
            this.ctx.strokeStyle = p.invalid ? '#D9534F' : '#0a381f'
            if (p.y < lastY) {
                this.ctx.strokeStyle = '#D9534F'
            }
            this.ctx.arc(p.x, p.y, 2, 0, 2 * Math.PI)
            this.ctx.stroke()

            lastY = p.y
        }
    }

    renderLine(vectors: TrajectoryPoint[], strokeStyle = '#000'): void {
        this.ctx.strokeStyle = strokeStyle
        this.ctx.beginPath()
        let moveTo = true
        for (const p of vectors) {
            if (moveTo) {
                this.ctx.moveTo(p.x, p.y)

                if (!p.invalid) {
                    moveTo = false
                }
            } else {
                this.ctx.lineTo(p.x, p.y)

                if (p.invalid) {
                    moveTo = true
                }
            }
        }
        this.ctx.stroke()
    }

    renderMDAxis(offset: number): void {
        const ticks = scaleLinear().domain([0, this.trajectory.maxMD]).ticks(10)

        this.ctx.beginPath()

        this.ctx.fillStyle = theme['gray-dark']
        this.ctx.textAlign = offset > 0 ? 'left' : 'right'
        const textSize = 11 / this.transform.k
        this.ctx.font = textSize + 'px arial'
        this.ctx.textBaseline = 'middle'

        for (const tick of ticks) {
            this.ctx.save()
            const p = this.trajectory.getPoint(tick, offset + (offset > 0 ? 12 : -12), 'md')
            this.ctx.translate(p.x, p.y)
            this.ctx.rotate(new paper.Point(p.vector).angleInRadians)
            this.ctx.fillText(tick.toString(), 0, 0)

            this.ctx.restore()
        }
    }

    public renderCasing(mdStart: number, mdEnd: number, distance: number, shoeWidth: number): void {
        const fillStyle = theme['gray-darker']
        const shoeHeight = shoeWidth * 1.17

        const body = {
            mdStart: mdStart,
            mdEnd: mdEnd,
            offsetStart: distance - shoeWidth + 2,
            offsetEnd: distance - shoeWidth * 0.5
        }

        const shoe = [
            { x: body.offsetStart, y: body.mdEnd },
            { x: distance, y: body.mdEnd },
            { x: body.offsetStart, y: body.mdEnd - shoeHeight }
        ]

        this.renderArea(body.mdStart, body.mdEnd, body.offsetStart, body.offsetEnd, fillStyle)
        this.renderArea(body.mdStart, body.mdEnd, -body.offsetStart, -body.offsetEnd, fillStyle)

        const renderCasing = this.trajectory.getPoint(mdEnd, 0, 'md').invalid === false
        if (renderCasing) {
            this.drawArea(shoe.map(p => this.trajectory.getPoint(p.y, p.x, 'md')))
            this.drawArea(shoe.map(p => this.trajectory.getPoint(p.y, -p.x, 'md')))
        }
    }

    get pixelRatio(): number {
        const dpr = window.devicePixelRatio || 1
        const bsr =
            this.ctx.webkitBackingStorePixelRatio ||
            this.ctx.mozBackingStorePixelRatio ||
            this.ctx.msBackingStorePixelRatio ||
            this.ctx.oBackingStorePixelRatio ||
            this.ctx.backingStorePixelRatio ||
            1

        return dpr / bsr
    }

    finalrestore(): void {
        this.ctx.restore()
    }
}
